import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import type { ApiClientError } from '@/lib/api-client';
import {
  fetchNotifications,
  fetchUnreadCount,
  markAllNotificationsRead,
  markNotificationRead,
} from './api';
import type { NotificationDto } from './types';

export const NOTIFICATIONS_QUERY_KEY = 'notifications' as const;

/** Unread-count badge — polled so a new notification surfaces without a reload. */
export const useUnreadCount = () =>
  useQuery<number, ApiClientError>({
    queryKey: [NOTIFICATIONS_QUERY_KEY, 'unread-count'],
    queryFn: fetchUnreadCount,
    refetchInterval: 60_000,
  });

/** The feed — only fetched while the dropdown is open. */
export const useNotifications = (enabled: boolean) =>
  useQuery<NotificationDto[], ApiClientError>({
    queryKey: [NOTIFICATIONS_QUERY_KEY, 'list'],
    queryFn: fetchNotifications,
    enabled,
  });

export const useMarkRead = () => {
  const qc = useQueryClient();
  return useMutation<void, ApiClientError, string>({
    mutationFn: (id) => markNotificationRead(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: [NOTIFICATIONS_QUERY_KEY] }),
  });
};

export const useMarkAllRead = () => {
  const qc = useQueryClient();
  return useMutation<void, ApiClientError, void>({
    mutationFn: () => markAllNotificationsRead(),
    onSuccess: () => qc.invalidateQueries({ queryKey: [NOTIFICATIONS_QUERY_KEY] }),
  });
};
